import { Component, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { TranslocoService } from '@ngneat/transloco';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ActionAlert } from '../../../../core/dto/actionAlert';
import { LoadingService } from '../../../../core/services/loading.service';
import { SettingService } from '../../../../core/services/setting.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-custom-domain',
  templateUrl: './custom-domain.component.html',
  styleUrls: ['./custom-domain.component.scss']
})
export class CustomDomainComponent implements OnInit {
  protected readonly environment = environment;

  customDomain = '';
  customDomainData: any;
  isCustomDomain = false;
  actionMessage = new ActionAlert();

  constructor(
    private settingService: SettingService,
    private loadingService: LoadingService,
    private nzModalService: NzModalService,
    private clipboard: Clipboard,
    private message: NzMessageService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.getCustomDomain().then();
  }

  async getCustomDomain(): Promise<void> {
    try {
      const response = await this.settingService.getCustomDomain();
      if (response?.success) {
        const data = response.data;
        this.customDomainData = data;
        this.isCustomDomain = this.hasCustomDomainData(data);
      }
    } catch (error: any) {
      this.isCustomDomain = false;
      this.actionMessage = new ActionAlert(
        true,
        true,
        error?.message || 'Failed to load custom domain',
        'error'
      );
    }
  }

  async getCustomHostnameInfo(): Promise<void> {
    this.loadingService.start();
    try {
      const response = await this.settingService.getCustomHostnameInfo();
      if (response?.success) {
        const data = response.data;
        this.customDomainData = data;
        this.isCustomDomain = this.hasCustomDomainData(data);
      }
      this.loadingService.stop();
    } catch (error: any) {
      this.loadingService.stop();
      this.isCustomDomain = false;
      this.actionMessage = new ActionAlert(
        true,
        true,
        error?.message || 'Failed to refresh custom hostname info',
        'error'
      );
    }
  }

  async onClickDomain(): Promise<void> {
    const normalized = this.normalizeCustomDomain(this.customDomain);
    if (!this.isValidCustomDomain(normalized)) {
      this.actionMessage = new ActionAlert(
        true,
        true,
        'Please enter a valid domain without www and without a trailing slash (e.g., example.com)',
        'error'
      );
      return;
    }

    this.customDomain = normalized;
    this.loadingService.start();
    try {
      const payload = { hostname: this.customDomain };
      const response = await this.settingService.addCustomDomain(payload);
      if (response?.success) {
        this.customDomainData = response?.data;
        this.isCustomDomain = true;
        this.actionMessage = new ActionAlert(
          true,
          true,
          response?.message || 'Custom domain added successfully',
          'success'
        );
      }
    } catch (error: any) {
      this.actionMessage = new ActionAlert(
        true,
        true,
        error?.message || 'Cloudflare custom hostname creation failed',
        'error'
      );
    } finally {
      this.loadingService.stop();
    }
  }

  async removeCustomDomain(): Promise<void> {
    this.nzModalService.confirm({
      nzTitle: this.translocoService.translate(
        'settings.preferences.customDomain.modal.removeTitle'
      ),
      nzContent: this.translocoService.translate(
        'settings.preferences.customDomain.modal.removeContent'
      ),
      nzOkText: this.translocoService.translate(
        'settings.preferences.customDomain.modal.removeOk'
      ),
      nzCancelText: this.translocoService.translate(
        'settings.preferences.customDomain.modal.removeCancel'
      ),
      nzOnOk: async () => {
        this.loadingService.start();
        try {
          const response = await this.settingService.removeCustomDomain();
          if (response?.success) {
            this.customDomainData = null;
            this.customDomain = '';
            this.isCustomDomain = false;
            this.actionMessage = new ActionAlert(
              true,
              true,
              response?.message || 'Custom domain removed successfully',
              'success'
            );
          }
        } catch (error: any) {
          this.actionMessage = new ActionAlert(
            true,
            true,
            error?.message || 'Failed to remove custom domain',
            'error'
          );
        } finally {
          this.loadingService.stop();
        }
      },
    });
  }

  hasCustomDomainData(data: any): boolean {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    if (typeof data === 'object') return Object.keys(data).length > 0;
    return true;
  }

  normalizeCustomDomain(value: string): string {
    return (value || '')
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/\/+$/, '')
      .toLowerCase();
  }

  isValidCustomDomain(value: string): boolean {
    if (!value || !value.trim()) return false;
    if (value.includes('/') || value.includes('\\')) return false;
    const normalized = this.normalizeCustomDomain(value);
    if (!normalized) return false;
    if (normalized.startsWith('www.')) return false;
    if (/[^a-z0-9.-@]/.test(normalized)) return false;
    if (/[.-@]{2,}/.test(normalized)) return false;
    return /^(?!-)[a-z0-9@-]+(\.[a-z0-9@-]+)*\.[a-z]{2,}(\.[a-z]{2,})?$/.test(normalized);
  }

  copyToClipboard(value: string): void {
    if (value) {
      this.clipboard.copy(value);
      this.message.success(
        this.translocoService.translate(
          'settings.preferences.customDomain.messages.copied'
        )
      );
    }
  }

  removeProtocol(url: string): string {
    return (url || '').replace(/^https?:\/\//i, '');
  }

  statusColors(status: string): string {
    if (status === 'active') return 'green';
    if (status === 'pending') return 'yellow';
    return 'grey';
  }

  statusColorsSsl(status: string): string {
    if (status === 'active') return 'green';
    if (status === 'pending_validation') return 'yellow';
    return 'grey';
  }
}
